/**
 * Dialog for editing a pending invite's role and team assignments
 */
import { Check, ChevronDown, X } from "lucide-react";
import { useState } from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
} from "@/components/ui/command";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { trpc } from "@/lib/trpc";
import { cn } from "@/lib/utils";

interface EditInviteDialogProps {
	invite: {
		id: string;
		email: string;
		accountRoleId: string;
		teamIds: string[];
		teamRoleId: string | null;
	};
	onClose: () => void;
	onSuccess: () => void;
}

/**
 * Dialog for editing a pending invite's configuration
 * @param root0 - Component props
 * @param root0.invite - The invite data to edit
 * @param root0.onClose - Callback when dialog is closed
 * @param root0.onSuccess - Callback when invite is successfully updated
 */
export function EditInviteDialog({
	invite,
	onClose,
	onSuccess,
}: EditInviteDialogProps) {
	const [selectedAccountRoleId, setSelectedAccountRoleId] = useState<string>(invite.accountRoleId);
	const [selectedTeamIds, setSelectedTeamIds] = useState<string[]>(invite.teamIds);
	const [selectedTeamRoleId, setSelectedTeamRoleId] = useState<string | null>(invite.teamRoleId);
	const [accountRoleSelectorOpen, setAccountRoleSelectorOpen] = useState(false);
	const [teamSelectorOpen, setTeamSelectorOpen] = useState(false);
	const [teamRoleSelectorOpen, setTeamRoleSelectorOpen] = useState(false);

	// Fetch roles for the account
	const rolesQuery = trpc.roles.list.useQuery();

	// Fetch teams for the account
	const teamsQuery = trpc.permissionGroups.listAccountTeams.useQuery();

	// Update invite mutation
	const updateInviteMutation = trpc.permissionGroups.updateInvite.useMutation({
		onSuccess: () => {
			onSuccess();
		},
	});

	/**
	 * Saves the updated invite configuration
	 */
	const handleSave = () => {
		updateInviteMutation.mutate({
			inviteId: invite.id,
			accountRoleId: selectedAccountRoleId,
			teamIds: selectedTeamIds.length > 0 ? selectedTeamIds : undefined,
			teamRoleId: selectedTeamIds.length > 0 ? selectedTeamRoleId : null,
		});
	};

	/**
	 * Toggles a team selection
	 * @param teamId - The ID of the team to toggle
	 */
	const handleToggleTeam = (teamId: string) => {
		setSelectedTeamIds((prev) =>
			prev.includes(teamId)
				? prev.filter((id) => id !== teamId)
				: [...prev, teamId]
		);
	};

	const selectedAccountRole = rolesQuery.data?.find((r) => r.id === selectedAccountRoleId);
	const selectedTeamRole = rolesQuery.data?.find((r) => r.id === selectedTeamRoleId);
	const selectedTeams = teamsQuery.data?.filter((t) => selectedTeamIds.includes(t.id)) ?? [];

	return (
		<Dialog open={true} onOpenChange={(open) => { if (!open) onClose(); }}>
			<DialogContent className="sm:max-w-[500px]">
				<DialogHeader>
					<DialogTitle>Edit Invite</DialogTitle>
					<DialogDescription>
						Update the role and team assignments for this pending invite.
					</DialogDescription>
				</DialogHeader>

				<form onSubmit={(e) => { e.preventDefault(); handleSave(); }}>
					<div className="space-y-4 py-4">
						{/* Email (read-only) */}
						<div className="space-y-2">
							<Label>Email</Label>
							<Input value={invite.email} readOnly className="bg-muted" />
							<p className="text-xs text-muted-foreground">
								Email cannot be changed. Revoke and create a new invite if needed.
							</p>
						</div>

						{/* Account Role (required) */}
						<div className="space-y-2">
							<Label>Account Role (required)</Label>
							<Popover open={accountRoleSelectorOpen} onOpenChange={setAccountRoleSelectorOpen}>
								<PopoverTrigger asChild>
									<Button type="button" variant="outline" className="w-full justify-between">
										{selectedAccountRole?.name ?? "Select role..."}
										<ChevronDown className="ml-2 h-4 w-4 opacity-50" />
									</Button>
								</PopoverTrigger>
								<PopoverContent className="w-[400px] p-0" align="start">
									<Command>
										<CommandInput placeholder="Search roles..." />
										<CommandList>
											<CommandEmpty>No roles found.</CommandEmpty>
											<CommandGroup>
												{rolesQuery.data?.map((role) => (
													<CommandItem
														key={role.id}
														onSelect={() => {
															setSelectedAccountRoleId(role.id);
															setAccountRoleSelectorOpen(false);
														}}
													>
														<Check
															className={cn(
																"mr-2 h-4 w-4",
																selectedAccountRoleId === role.id
																	? "opacity-100"
																	: "opacity-0"
															)}
														/>
														<div className="flex flex-col">
															<span>{role.name}</span>
															{role.description && (
																<span className="text-xs text-muted-foreground">
																	{role.description}
																</span>
															)}
														</div>
														{role.isSystem && (
															<Badge variant="outline" className="ml-auto text-xs">
																System
															</Badge>
														)}
													</CommandItem>
												))}
											</CommandGroup>
										</CommandList>
									</Command>
								</PopoverContent>
							</Popover>
						</div>

						{/* Teams (optional) */}
						<div className="space-y-2">
							<Label>Teams (optional)</Label>
							<div className="flex flex-wrap gap-2 min-h-[32px]">
								{selectedTeams.length === 0 ? (
									<span className="text-muted-foreground text-sm">No teams selected</span>
								) : (
									selectedTeams.map((team) => (
										<Badge key={team.id} variant="secondary" className="pr-1">
											{team.name}
											<button
												type="button"
												className="ml-1 rounded-full hover:bg-muted p-0.5"
												onClick={() => { handleToggleTeam(team.id); }}
											>
												<X className="h-3 w-3" />
											</button>
										</Badge>
									))
								)}
							</div>
							<Popover open={teamSelectorOpen} onOpenChange={setTeamSelectorOpen}>
								<PopoverTrigger asChild>
									<Button type="button" variant="outline" className="w-full justify-between">
										Select teams...
										<ChevronDown className="ml-2 h-4 w-4 opacity-50" />
									</Button>
								</PopoverTrigger>
								<PopoverContent className="w-[400px] p-0" align="start">
									<Command>
										<CommandInput placeholder="Search teams..." />
										<CommandList>
											<CommandEmpty>No teams found.</CommandEmpty>
											<CommandGroup>
												{teamsQuery.data?.map((team) => (
													<CommandItem
														key={team.id}
														onSelect={() => { handleToggleTeam(team.id); }}
													>
														<Check
															className={cn(
																"mr-2 h-4 w-4",
																selectedTeamIds.includes(team.id)
																	? "opacity-100"
																	: "opacity-0"
															)}
														/>
														{team.name}
													</CommandItem>
												))}
											</CommandGroup>
										</CommandList>
									</Command>
								</PopoverContent>
							</Popover>
						</div>

						{/* Team Role (required if teams selected) */}
						{selectedTeamIds.length > 0 && (
							<div className="space-y-2">
								<Label>Team Role (required)</Label>
								<Popover open={teamRoleSelectorOpen} onOpenChange={setTeamRoleSelectorOpen}>
									<PopoverTrigger asChild>
										<Button type="button" variant="outline" className="w-full justify-between">
											{selectedTeamRole?.name ?? "Select team role..."}
											<ChevronDown className="ml-2 h-4 w-4 opacity-50" />
										</Button>
									</PopoverTrigger>
									<PopoverContent className="w-[400px] p-0" align="start">
										<Command>
											<CommandInput placeholder="Search roles..." />
											<CommandList>
												<CommandEmpty>No roles found.</CommandEmpty>
												<CommandGroup>
													{rolesQuery.data?.map((role) => (
														<CommandItem
															key={role.id}
															onSelect={() => {
																setSelectedTeamRoleId(role.id);
																setTeamRoleSelectorOpen(false);
															}}
														>
															<Check
																className={cn(
																	"mr-2 h-4 w-4",
																	selectedTeamRoleId === role.id
																		? "opacity-100"
																		: "opacity-0"
																)}
															/>
															<div className="flex flex-col">
																<span>{role.name}</span>
																{role.description && (
																	<span className="text-xs text-muted-foreground">
																		{role.description}
																	</span>
																)}
															</div>
														</CommandItem>
													))}
												</CommandGroup>
											</CommandList>
										</Command>
									</PopoverContent>
								</Popover>
								<p className="text-xs text-muted-foreground">
									User will have this role in all selected teams when they accept
								</p>
							</div>
						)}

						{/* Error */}
						{updateInviteMutation.isError && (
							<div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
								{updateInviteMutation.error.message}
							</div>
						)}
					</div>

					<DialogFooter>
						<Button type="button" variant="outline" onClick={onClose}>
							Cancel
						</Button>
						<Button
							type="submit"
							disabled={
								!selectedAccountRoleId ||
								(selectedTeamIds.length > 0 && !selectedTeamRoleId) ||
								updateInviteMutation.isPending
							}
						>
							{updateInviteMutation.isPending ? "Saving..." : "Save Changes"}
						</Button>
					</DialogFooter>
				</form>
			</DialogContent>
		</Dialog>
	);
}
